/**
 * Right-dock Motion tab: in / emphasis / out animation for the selected
 * layer(s). Writes `craftaraAnim` on the Fabric objects, so animation edits
 * go through normal canvas history and undo.
 *
 * Sliders write to the objects live but only commit history on release, so
 * one drag is one undo step.
 */

import { useEffect, useReducer, useState } from 'react';
import type { fabric } from 'fabric';
import type {
  AnimationChannel,
  AnimationDirection,
  AnimationPresetId,
  EasingId,
  LayerAnimation,
  LayerAnimationStep,
  LayerEmphasisStep,
} from '@canvas/shared-types';
import { presetDef } from '@/data/animationPresets';
import { readLayerAnimation, writeLayerAnimation } from '@/lib/video/layerAnimation';
import { isGuideObject } from '@/lib/guideLines';
import { useProjectStore } from '@/stores/projectStore';
import { useVideoStore } from '@/stores/videoStore';
import AnimationPresetGrid from './AnimationPresetGrid';

interface AnimationPanelProps {
  canvas: fabric.Canvas | null;
  ready: boolean;
  onChange: () => void;
}

const CHANNELS: Array<{ id: AnimationChannel; label: string }> = [
  { id: 'in', label: 'In' },
  { id: 'emphasis', label: 'Emphasis' },
  { id: 'out', label: 'Out' },
];

const EASING_OPTIONS: Array<{ id: EasingId; label: string }> = [
  { id: 'linear', label: 'Linear' },
  { id: 'ease-in', label: 'Ease in' },
  { id: 'ease-out', label: 'Ease out' },
  { id: 'ease-in-out', label: 'Ease in-out' },
  { id: 'smoothstep', label: 'Smooth' },
  { id: 'back-out', label: 'Back out' },
  { id: 'spring-soft', label: 'Spring' },
];

const DIRECTIONS: Array<{ id: AnimationDirection; label: string; title: string }> = [
  { id: 'left', label: '←', title: 'Left' },
  { id: 'right', label: '→', title: 'Right' },
  { id: 'up', label: '↑', title: 'Top' },
  { id: 'down', label: '↓', title: 'Bottom' },
];

function selectedLayers(canvas: fabric.Canvas | null): fabric.Object[] {
  if (!canvas) return [];
  return canvas.getActiveObjects().filter((o) => !isGuideObject(o));
}

function stepFor(
  channel: AnimationChannel,
  id: AnimationPresetId,
  prev: LayerAnimationStep | undefined
): LayerAnimationStep | LayerEmphasisStep | undefined {
  if (id === 'none') return undefined;
  const def = presetDef(id);
  if (!def) return undefined;
  const step: LayerAnimationStep = {
    preset: id,
    durationMs: def.defaultDurationMs,
    delayMs: prev?.delayMs ?? 0,
    easing: def.defaultEasing,
    intensity: prev?.intensity ?? 1,
  };
  if (def.supportsDirection) step.direction = prev?.direction ?? 'left';
  if (channel === 'emphasis') {
    // Ken Burns is a one-way push across the scene; the rest are cyclic.
    return { ...step, loop: id !== 'ken-burns' };
  }
  return step;
}

export default function AnimationPanel({ canvas, ready, onChange }: AnimationPanelProps) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [dirty, setDirty] = useState(false);
  const activePageId = useProjectStore((s) => s.currentProject?.activePageId);
  const sceneMs = useVideoStore(
    (s) => s.video?.scenes.find((sc) => sc.pageId === activePageId)?.durationMs ?? 0
  );

  useEffect(() => {
    if (!canvas || !ready) return;
    const events = ['selection:created', 'selection:updated', 'selection:cleared'] as const;
    events.forEach((ev) => canvas.on(ev, rerender));
    return () => events.forEach((ev) => canvas.off(ev, rerender));
  }, [canvas, ready]);

  const targets = selectedLayers(canvas);
  if (!targets.length) {
    return (
      <div className="ps-props vt-anim-panel">
        <h3>Motion</h3>
        <p className="ps-props-empty">Select a layer to animate how it enters, moves and exits.</p>
      </div>
    );
  }

  const anim: LayerAnimation = readLayerAnimation(targets[0]) ?? { v: 1 };

  const write = (next: LayerAnimation, commit: boolean): void => {
    targets.forEach((obj) => writeLayerAnimation(obj, next));
    rerender();
    if (commit) {
      onChange();
      setDirty(false);
    } else {
      setDirty(true);
    }
  };
  const commitIfDirty = (): void => {
    if (dirty) {
      onChange();
      setDirty(false);
    }
  };
  const setStep = (channel: AnimationChannel, step: LayerAnimationStep | undefined, commit: boolean) => {
    const next: LayerAnimation = { ...anim };
    if (step) (next as Record<AnimationChannel, LayerAnimationStep | undefined>)[channel] = step;
    else delete next[channel];
    write(next, commit);
  };

  const preview = (): void => {
    const store = useVideoStore.getState();
    const cue = store.cues.find((c) => c.pageId === activePageId);
    if (cue) store.cueTo(cue.startMs);
    store.play();
  };

  return (
    <div className="ps-props vt-anim-panel">
      <h3>
        Motion{targets.length > 1 ? ` · ${targets.length} layers` : ''}
      </h3>

      {CHANNELS.map(({ id: channel, label }) => {
        const step = anim[channel];
        const def = step ? presetDef(step.preset) : undefined;
        return (
          <section key={channel} className="vt-anim-section">
            <h4>{label}</h4>
            <AnimationPresetGrid
              channel={channel}
              value={step?.preset ?? 'none'}
              onPick={(pid) => setStep(channel, stepFor(channel, pid, step), true)}
            />
            {step && (
              <div className="vt-anim-controls" onPointerUp={commitIfDirty} onKeyUp={commitIfDirty}>
                <label className="vt-anim-field">
                  <span>Duration</span>
                  <input
                    type="range"
                    min={100}
                    max={channel === 'emphasis' ? 10_000 : 4000}
                    step={50}
                    value={step.durationMs}
                    onChange={(e) =>
                      setStep(channel, { ...step, durationMs: Number(e.target.value) }, false)
                    }
                  />
                  <output>{(step.durationMs / 1000).toFixed(2)}s</output>
                </label>
                {channel !== 'emphasis' && (
                  <label className="vt-anim-field">
                    <span>{channel === 'in' ? 'Delay' : 'Ends before'}</span>
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, sceneMs - step.durationMs)}
                      step={50}
                      value={Math.min(step.delayMs, Math.max(0, sceneMs - step.durationMs))}
                      onChange={(e) =>
                        setStep(channel, { ...step, delayMs: Number(e.target.value) }, false)
                      }
                    />
                    <output>{(step.delayMs / 1000).toFixed(2)}s</output>
                  </label>
                )}
                {def?.supportsDirection && (
                  <div className="vt-anim-field">
                    <span>{channel === 'in' ? 'From' : 'Toward'}</span>
                    <div className="vt-anim-dirs">
                      {DIRECTIONS.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          title={d.title}
                          className={step.direction === d.id ? 'active' : ''}
                          onClick={() => setStep(channel, { ...step, direction: d.id }, true)}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <label className="vt-anim-field">
                  <span>Easing</span>
                  <select
                    value={step.easing}
                    onChange={(e) =>
                      setStep(channel, { ...step, easing: e.target.value as EasingId }, true)
                    }
                  >
                    {EASING_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {step.preset !== 'fade-in' && step.preset !== 'fade-out' && step.preset !== 'spin-slow' && (
                  <label className="vt-anim-field">
                    <span>Intensity</span>
                    <input
                      type="range"
                      min={0.1}
                      max={2}
                      step={0.1}
                      value={step.intensity ?? 1}
                      onChange={(e) =>
                        setStep(channel, { ...step, intensity: Number(e.target.value) }, false)
                      }
                    />
                    <output>{(step.intensity ?? 1).toFixed(1)}×</output>
                  </label>
                )}
              </div>
            )}
          </section>
        );
      })}

      <div className="ps-props-actions">
        <button
          type="button"
          className="vt-anim-preview"
          title="Play this scene on the canvas (Space)"
          onClick={preview}
        >
          ▶ Preview scene
        </button>
      </div>
      {sceneMs > 0 && (
        <p className="ps-props-hint">
          Scene length {(sceneMs / 1000).toFixed(1)}s · change it on the timeline.
        </p>
      )}
    </div>
  );
}
